import type { AttributeDefinition, ContentTypeId, WorkAttributeValue } from "@/types/taxonomy";

/**
 * 作品属性の表示ルール。
 *
 * - 検索条件として使う属性（商品形式ごとの attributeIds）だけを対象にする
 * - 値が無い・不明・0 のものは表示しない（「0分」「0ページ」と誤解させない）
 * - 数値属性は数値として解釈できたものだけを表示する
 * - 表示形式は検索条件と同じ定義（attributeId・単位）から作る
 */

export interface DisplayAttribute {
  /** 属性定義ID（検索条件の attributes[].attributeId と同じ値） */
  id: string;
  name: string;
  /** 生の値 */
  value: string;
  /** 単位込みの表示用文字列（例: 「118分」「224ページ」「150mm」「HD」） */
  display: string;
  unit: string | null;
}

export interface ResolveAttributesOptions {
  contentType: ContentTypeId;
  /** その商品形式で使う属性ID（表示順）。空配列なら形式の contentTypeIds で判定する */
  allowedIds?: string[];
}

const INVALID_TOKENS = new Set(["", "0", "00", "不明", "未設定", "未登録", "-", "ー", "n/a", "na", "none", "null"]);

function isUsableToken(value: string): boolean {
  const token = value.trim();
  if (!token) return false;
  return !INVALID_TOKENS.has(token.toLowerCase());
}

function toNumber(value: string): number | null {
  const normalized = value.trim().replace(/,/g, "");
  const match = normalized.match(/-?\d+(\.\d+)?/);
  if (!match) return null;
  const parsed = Number(match[0]);
  if (!Number.isFinite(parsed) || parsed <= 0) return null;
  return parsed;
}

function formatNumber(value: number): string {
  return Number.isInteger(value) ? String(value) : value.toFixed(1).replace(/\.0$/, "");
}

function formatValue(definition: AttributeDefinition, rawValue: string): string | null {
  const value = rawValue.trim();
  if (!isUsableToken(value)) return null;

  if (definition.valueType === "number") {
    const parsed = toNumber(value);
    if (parsed === null) return null;
    return definition.unit ? `${formatNumber(parsed)}${definition.unit}` : formatNumber(parsed);
  }

  if (definition.unit && !value.endsWith(definition.unit)) {
    return `${value}${definition.unit}`;
  }
  return value;
}

/** 作品が持つ属性から、その商品形式で表示してよいものだけを返す */
export function resolveDisplayAttributes(
  values: WorkAttributeValue[],
  definitions: AttributeDefinition[],
  options: ResolveAttributesOptions,
): DisplayAttribute[] {
  if (!values || values.length === 0) return [];
  const allowedIds = options.allowedIds ?? [];
  const allowedSet = allowedIds.length > 0 ? new Set(allowedIds) : null;
  const definitionById = new Map(definitions.map((definition) => [definition.id, definition]));

  const resolved: DisplayAttribute[] = [];
  const seen = new Set<string>();

  values.forEach((attribute) => {
    if (!attribute || typeof attribute.attributeId !== "string") return;
    if (seen.has(attribute.attributeId)) return;
    const definition = definitionById.get(attribute.attributeId);
    if (!definition) return;

    if (allowedSet) {
      if (!allowedSet.has(definition.id)) return;
    } else if (
      definition.contentTypeIds.length > 0 &&
      !definition.contentTypeIds.includes(options.contentType)
    ) {
      return;
    }

    const display = formatValue(definition, String(attribute.value ?? ""));
    if (!display) return;

    seen.add(definition.id);
    resolved.push({
      id: definition.id,
      name: definition.name,
      value: String(attribute.value ?? "").trim(),
      display,
      unit: definition.unit,
    });
  });

  if (allowedIds.length === 0) return resolved;
  const order = new Map(allowedIds.map((id, index) => [id, index]));
  return resolved.sort((a, b) => (order.get(a.id) ?? 999) - (order.get(b.id) ?? 999));
}